import React, { useState } from "react";
import { useLingui } from "@lingui/react";
import { t } from "@lingui/core/macro";
import { Weekday, weekdays } from "../Weekdays.ts";
import { TimeSlot } from "../../api/scheduleApi.ts";
import ActionButton from "../ActionButton.tsx";
import Container from "../Container.tsx";
import ContainerHeader from "../ContainerHeader.tsx";
import FormField from "../FormField.tsx"; // Import the new FormField component
import TimeInputField from "../TimeInputField.tsx";
import { LocationWithName } from "../../api/locationApi.ts";

interface TimeslotFormProps {
  onAddTimeslot: (newTimeSlot: TimeSlot) => void;
  existingTimeslots: TimeSlot[] | undefined;
  isLoading: boolean;
  onSelectLocation: (location: LocationWithName) => void;
  selectedLocation: LocationWithName;
  locations: LocationWithName[];
  hideLocation?: boolean;
}

const generateTimeOptions = (limit: number) => {
  return Array.from({ length: limit }, (_, i) => String(i).padStart(2, "0"));
};

const hours = generateTimeOptions(24);
const minutes = generateTimeOptions(60);

const TimeslotForm: React.FC<TimeslotFormProps> = ({
  onAddTimeslot,
  existingTimeslots,
  isLoading,
  onSelectLocation,
  selectedLocation,
  locations,
  hideLocation,
}) => {
  const { i18n } = useLingui();
  const [weekday, setWeekday] = useState<Weekday>("MONDAY");
  const [startHour, setStartHour] = useState("09");
  const [startMinute, setStartMinute] = useState("00");
  const [endHour, setEndHour] = useState("17");
  const [endMinute, setEndMinute] = useState("00");
  const [error, setError] = useState<string | null>(null);

  const handleSetLocation = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onSelectLocation(
      locations.filter(({ name }) => e.target.value === name)[0],
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const startTime = parseInt(startHour) * 60 + parseInt(startMinute);
    const endTime = parseInt(endHour) * 60 + parseInt(endMinute);

    if (endTime <= startTime) {
      setError(i18n._(t`End time must be after start time.`));
      return;
    }

    const newTimeslotStart = startTime;
    const newTimeslotEnd = endTime;

    const overlaps = existingTimeslots?.some((existingTimeslot) => {
      if (existingTimeslot.weekday !== weekday) {
        return false;
      }

      const existingStart =
        parseInt(existingTimeslot.startHour) * 60 +
        parseInt(existingTimeslot.startMinute);
      const existingEnd =
        parseInt(existingTimeslot.endHour) * 60 +
        parseInt(existingTimeslot.endMinute);

      return newTimeslotStart < existingEnd && newTimeslotEnd > existingStart;
    });

    if (overlaps) {
      setError(i18n._(t`This timeslot overlaps with an existing one.`));
      return;
    }

    const newTimeslot: TimeSlot = {
      id: window.crypto.randomUUID(),
      locationId: selectedLocation.id,
      weekday,
      startHour,
      startMinute,
      endHour,
      endMinute,
    };
    onAddTimeslot(newTimeslot);
    setWeekday("MONDAY");
    setStartHour("09");
    setStartMinute("00");
    setEndHour("17");
    setEndMinute("00");
  };

  return (
    <Container className={"mb-4"}>
      <ContainerHeader>{i18n._(t`Add New Timeslot`)}</ContainerHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Location field - now at the top */}
        {!hideLocation && (
          <FormField htmlFor="location" label={i18n._(t`Location`)}>
            <select
              id="location"
              name="location"
              value={selectedLocation?.name || ""}
              onChange={handleSetLocation}
              className="focus:border-soluud-primary focus:ring-soluud-primary/30 block w-full rounded-lg border border-gray-300 py-2 pr-10 pl-3 text-base transition-colors duration-150 focus:ring-2 focus:outline-none sm:text-sm"
            >
              {locations.map((loc) => (
                <option key={loc.id} value={loc.name}>
                  {loc.name}
                </option>
              ))}
            </select>
          </FormField>
        )}

        {/* Other fields in a grid */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField htmlFor="weekday" label={i18n._(t`Weekday`)}>
            <select
              id="weekday"
              name="weekday"
              value={weekday}
              onChange={(e) => setWeekday(e.target.value as Weekday)}
              className="focus:border-soluud-primary focus:ring-soluud-primary/30 block w-full rounded-lg border border-gray-300 py-2 pr-10 pl-3 text-base transition-colors duration-150 focus:ring-2 focus:outline-none sm:text-sm"
            >
              {weekdays.map((day) => (
                <option key={day.value} value={day.value}>
                  {i18n._(day.label)}
                </option>
              ))}
            </select>
          </FormField>

          <div className="grid grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-2">
            <FormField htmlFor="startHour" label={i18n._(t`Start Time`)}>
              <div className="flex gap-2">
                <TimeInputField
                  id="startHour"
                  value={startHour}
                  onChange={(e) => setStartHour(e.target.value)}
                  options={hours}
                />
                <TimeInputField
                  id="startMinute"
                  value={startMinute}
                  onChange={(e) => setStartMinute(e.target.value)}
                  options={minutes}
                />
              </div>
            </FormField>

            <FormField htmlFor="endHour" label={i18n._(t`End Time`)}>
              <div className="flex gap-2">
                <TimeInputField
                  id="endHour"
                  value={endHour}
                  onChange={(e) => setEndHour(e.target.value)}
                  options={hours}
                />
                <TimeInputField
                  id="endMinute"
                  value={endMinute}
                  onChange={(e) => setEndMinute(e.target.value)}
                  options={minutes}
                />
              </div>
            </FormField>
          </div>
        </div>

        <div className="pt-2">
          {error && <p className="mb-2 text-sm text-red-500">{error}</p>}
          <ActionButton
            type="submit"
            disabled={isLoading}
            isLoading={isLoading}
          >
            {i18n._(t`Add Timeslot`)}
          </ActionButton>
        </div>
      </form>
    </Container>
  );
};

export default TimeslotForm;
